import React, { useMemo, useState } from 'react';
import { CompleteKundali, Language, PlanetName, PlanetPosition } from '../../types/kundali';
import { getSpecialDrishtiHouses } from '../../utils/dashaGocharSynthesisEngine';
import { calculateHouseDistance } from '../../utils/gocharVedhaEngine';
import { getPlanetShortCode, ZODIAC_HI } from '../../utils/language';
import { 
  Grid, 
  Sparkles, 
  Eye, 
  MapPin, 
  Compass, 
  ChevronRight, 
  Info, 
  Layers,
  HelpCircle,
  X
} from 'lucide-react';
import { AshtakavargaGocharInspector } from './AshtakavargaGocharInspector';

export type GocharPerspective = 'lagna' | 'moon' | 'sun';

export interface GocharDrishtiMatrixProps {
  kundali: CompleteKundali;
  transitPlanets: PlanetPosition[];
  selectedPerspective: GocharPerspective;
  onSelectPerspective?: (perspective: GocharPerspective) => void;
  selectedTransitPlanet?: PlanetName | null;
  onSelectTransitPlanet?: (planet: PlanetName) => void;
  lang?: Language;
  onOpenEvidenceDrawer?: (planet?: PlanetName) => void;
}

export interface MatrixCellData {
  transitPlanet: PlanetName;
  natalHouse: number;
  occupies: boolean;
  drishti: boolean;
  drishtiLabel?: string;
  yutiNatalPlanets: string[];
  drishtiNatalPlanets: string[];
  hasRelationship: boolean;
  transitSign: string;
  transitDegree: string;
  isRetrograde: boolean;
  transitLongitude: number;
}

export const GocharDrishtiMatrix: React.FC<GocharDrishtiMatrixProps> = ({
  kundali,
  transitPlanets,
  selectedPerspective,
  onSelectPerspective,
  selectedTransitPlanet,
  onSelectTransitPlanet,
  lang = 'en',
  onOpenEvidenceDrawer
}) => {
  const isHi = lang === 'hi';
  const [selectedCell, setSelectedCell] = useState<{ planet: PlanetName; house: number } | null>(null);
  const [mobileViewPlanet, setMobileViewPlanet] = useState<PlanetName>('Jupiter');

  // Authoritative reference sign index based on selected perspective
  const lagnaHouse = kundali.houses[0];
  const lagnaSignIndex = lagnaHouse ? lagnaHouse.signIndex : 0;
  const lagnaSignName = lagnaHouse ? lagnaHouse.sign : 'Aries';

  const moonPlanet = kundali.planets.find(p => p.name === 'Moon');
  const moonSignIndex = moonPlanet ? moonPlanet.signIndex : 0;
  const moonSignName = moonPlanet ? moonPlanet.sign : 'Aries';

  const sunPlanet = kundali.planets.find(p => p.name === 'Sun');
  const sunSignIndex = sunPlanet ? sunPlanet.signIndex : 0;
  const sunSignName = sunPlanet ? sunPlanet.sign : 'Aries';

  const referenceSignIndex = useMemo(() => {
    if (selectedPerspective === 'moon') return moonSignIndex;
    if (selectedPerspective === 'sun') return sunSignIndex;
    return lagnaSignIndex;
  }, [selectedPerspective, moonSignIndex, sunSignIndex, lagnaSignIndex]);

  const referenceSignName = useMemo(() => {
    if (selectedPerspective === 'moon') return moonSignName;
    if (selectedPerspective === 'sun') return sunSignName;
    return lagnaSignName;
  }, [selectedPerspective, moonSignName, sunSignName, lagnaSignName]);

  const perspectiveLabel = useMemo(() => {
    if (selectedPerspective === 'moon') return isHi ? `चंद्र लग्न (${ZODIAC_HI[moonSignName] || moonSignName})` : `Chandra Lagna (${moonSignName})`;
    if (selectedPerspective === 'sun') return isHi ? `सूर्य लग्न (${ZODIAC_HI[sunSignName] || sunSignName})` : `Surya Lagna (${sunSignName})`;
    return isHi ? `जन्म लग्न (${ZODIAC_HI[lagnaSignName] || lagnaSignName})` : `Janma Lagna (${lagnaSignName})`;
  }, [selectedPerspective, isHi, moonSignName, sunSignName, lagnaSignName]);

  const all9Planets: PlanetName[] = [
    'Sun',
    'Moon',
    'Mars',
    'Mercury',
    'Jupiter',
    'Venus',
    'Saturn',
    'Rahu',
    'Ketu'
  ];

  // Build the complete 9 x 12 deterministic matrix dataset
  const matrixData = useMemo(() => {
    return all9Planets.map((planetName) => {
      const tp = transitPlanets.find(p => p.name === planetName);
      if (!tp) {
        return {
          planet: planetName,
          symbol: getPlanetShortCode(planetName, lang),
          sign: 'Aries',
          degreeFormatted: '00° 00\'',
          isRetrograde: false,
          placedHouse: 1,
          cells: [] as MatrixCellData[]
        };
      }

      const placedHouse = calculateHouseDistance(referenceSignIndex, tp.signIndex);
      const drishtiHouseIndices = getSpecialDrishtiHouses(planetName, placedHouse);

      const cells: MatrixCellData[] = [];

      for (let h = 1; h <= 12; h++) {
        const occupies = placedHouse === h;
        const drishti = drishtiHouseIndices.includes(h);

        let drishtiLabel: string | undefined = undefined;
        if (drishti) {
          const aspectDistance = ((h - placedHouse + 12) % 12) + 1;
          if (aspectDistance === 7) {
            drishtiLabel = isHi ? '७म पूर्ण दृष्टि' : '7th Drishti';
          } else if (planetName === 'Mars' && aspectDistance === 4) {
            drishtiLabel = isHi ? '४थ विशेष दृष्टि' : '4th Drishti';
          } else if (planetName === 'Mars' && aspectDistance === 8) {
            drishtiLabel = isHi ? '८म विशेष दृष्टि' : '8th Drishti';
          } else if ((planetName === 'Jupiter' || planetName === 'Rahu' || planetName === 'Ketu') && aspectDistance === 5) {
            drishtiLabel = isHi ? '५म त्रिकोण दृष्टि' : '5th Drishti';
          } else if ((planetName === 'Jupiter' || planetName === 'Rahu' || planetName === 'Ketu') && aspectDistance === 9) {
            drishtiLabel = isHi ? '९म त्रिकोण दृष्टि' : '9th Drishti';
          } else if (planetName === 'Saturn' && aspectDistance === 3) {
            drishtiLabel = isHi ? '३री विशेष दृष्टि' : '3rd Drishti';
          } else if (planetName === 'Saturn' && aspectDistance === 10) {
            drishtiLabel = isHi ? '१०म विशेष दृष्टि' : '10th Drishti';
          } else {
            drishtiLabel = isHi ? `${aspectDistance}म दृष्टि` : `${aspectDistance}th Drishti`;
          }
        }

        // Map natal planets in this house (relative to current perspective)
        const yutiNatalPlanets: string[] = [];
        const drishtiNatalPlanets: string[] = [];

        (kundali.planets || []).forEach((np) => {
          if (np.name !== 'Ascendant') {
            const npHouseInPerspective = calculateHouseDistance(referenceSignIndex, np.signIndex);
            if (npHouseInPerspective === h) {
              if (occupies) {
                yutiNatalPlanets.push(np.name);
              }
              if (drishti) {
                drishtiNatalPlanets.push(np.name);
              }
            }
          }
        });

        const hasRelationship = occupies || drishti || yutiNatalPlanets.length > 0;

        cells.push({
          transitPlanet: planetName,
          natalHouse: h,
          occupies,
          drishti,
          drishtiLabel,
          yutiNatalPlanets,
          drishtiNatalPlanets,
          hasRelationship,
          transitSign: tp.sign,
          transitDegree: tp.degreeFormatted,
          isRetrograde: tp.isRetrograde,
          transitLongitude: tp.longitude
        });
      }

      return {
        planet: planetName,
        symbol: getPlanetShortCode(planetName, lang),
        sign: tp.sign,
        degreeFormatted: tp.degreeFormatted,
        isRetrograde: tp.isRetrograde,
        placedHouse,
        cells
      };
    });
  }, [all9Planets, transitPlanets, referenceSignIndex, kundali.planets, lang, isHi]);

  // Handle cell selection
  const handleCellClick = (planet: PlanetName, house: number) => {
    setSelectedCell({ planet, house });
    if (onSelectTransitPlanet) {
      onSelectTransitPlanet(planet);
    }
  };

  // Active inspected cell data
  const activeInspectedCell: MatrixCellData | null = useMemo(() => {
    if (!selectedCell) return null;
    const row = matrixData.find(r => r.planet === selectedCell.planet);
    if (!row) return null;
    return row.cells[selectedCell.house - 1] || null;
  }, [selectedCell, matrixData]);

  return (
    <div className="bg-astro-bg border border-astro-border rounded-2xl p-4 sm:p-6 shadow-sm space-y-4">
      {/* Header & Perspective Selector Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 border-b border-astro-border pb-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-astro-primary/10 border border-astro-primary/20 flex items-center justify-center text-astro-primary">
              <Grid className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-astro-primary font-serif text-lg">
                {isHi ? 'शास्त्रीय गोचर दृष्टि एवं संबंध मैट्रिक्स (9×12)' : 'Classical Gochar Drishti & Sambandha Matrix (9×12)'}
              </h3>
              <p className="text-xs text-astro-secondary-text">
                {isHi 
                  ? `गोचर ग्रह ➔ जन्म भाव (1-12) एवं जन्म ग्रह संबंध | आधार: ${perspectiveLabel}` 
                  : `Transit Grahas ➔ Natal Houses (1–12) & Natal Planets | Reference: ${perspectiveLabel}`}
              </p>
            </div>
          </div>
        </div>

        {/* Perspective Switcher Controls */}
        <div className="flex items-center flex-wrap gap-2">
          {onSelectPerspective && (
            <div className="flex items-center bg-astro-card border border-astro-border rounded-xl p-1 text-xs">
              <button
                onClick={() => onSelectPerspective('lagna')}
                className={`px-3 py-1 rounded-lg font-semibold transition-all ${
                  selectedPerspective === 'lagna'
                    ? 'bg-astro-primary text-white shadow-xs font-bold'
                    : 'text-astro-secondary-text hover:text-astro-text'
                }`}
              >
                {isHi ? 'लग्न' : 'Janma Lagna'}
              </button>
              <button
                onClick={() => onSelectPerspective('moon')}
                className={`px-3 py-1 rounded-lg font-semibold transition-all ${
                  selectedPerspective === 'moon'
                    ? 'bg-astro-primary text-white shadow-xs font-bold'
                    : 'text-astro-secondary-text hover:text-astro-text'
                }`}
              >
                {isHi ? 'चंद्र' : 'Chandra Lagna'}
              </button>
              <button
                onClick={() => onSelectPerspective('sun')}
                className={`px-3 py-1 rounded-lg font-semibold transition-all ${
                  selectedPerspective === 'sun'
                    ? 'bg-astro-primary text-white shadow-xs font-bold'
                    : 'text-astro-secondary-text hover:text-astro-text'
                }`}
              >
                {isHi ? 'सूर्य' : 'Surya Lagna'}
              </button>
            </div>
          )}

          {onOpenEvidenceDrawer && (
            <button
              onClick={() => onOpenEvidenceDrawer(selectedTransitPlanet || 'Jupiter')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-astro-card hover:bg-astro-border border border-astro-border text-astro-text text-xs font-semibold transition-colors"
              title={isHi ? 'शास्त्रीय प्रमाण देखें' : 'View Classical Evidence'}
            >
              <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'प्रमाण' : 'Evidence'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Legend & Semantic Guide */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs bg-astro-card/60 p-2.5 rounded-xl border border-astro-border/80">
        <div className="flex items-center flex-wrap gap-3">
          <span className="text-astro-secondary-text font-semibold text-[11px]">
            {isHi ? 'संबंध संकेत:' : 'Relationship Legend:'}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-900 dark:text-emerald-200 font-bold text-[10.5px]">
            <MapPin className="w-3 h-3 text-emerald-600" />
            <span>{isHi ? 'स्थान (Occupies)' : 'Occupies (Sthana)'}</span>
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-purple-500/15 border border-purple-500/30 text-purple-900 dark:text-purple-200 font-bold text-[10.5px]">
            <Eye className="w-3 h-3 text-purple-600" />
            <span>{isHi ? 'दृष्टि (Parashari Drishti)' : 'Drishti (Aspect)'}</span>
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/15 border border-amber-500/30 text-amber-900 dark:text-amber-200 font-bold text-[10.5px]">
            <Sparkles className="w-3 h-3 text-amber-600" />
            <span>{isHi ? 'युति (Yuti with Natal Graha)' : 'Yuti (Natal Conjunction)'}</span>
          </span>
        </div>
        <span className="text-[10.5px] text-astro-secondary-text italic">
          {isHi ? 'किसी भी सेल पर क्लिक करके विस्तृत संबंध देखें' : 'Click any cell to inspect relationship'}
        </span>
      </div>

      {/* 1. Desktop & Tablet 9x12 Responsive Matrix */}
      <div className="hidden sm:block overflow-x-auto border border-astro-border rounded-xl">
        <table className="w-full text-left text-xs border-collapse min-w-[760px]" role="table" aria-label="Classical Gochar Drishti Matrix">
          <thead>
            <tr className="bg-astro-card border-b border-astro-border text-astro-secondary-text text-[11px] font-bold">
              <th className="py-2.5 px-3 sticky left-0 z-20 bg-astro-card border-r border-astro-border min-w-[130px]" scope="col">
                {isHi ? 'गोचर ग्रह' : 'Transit Graha'}
              </th>
              {Array.from({ length: 12 }).map((_, idx) => {
                const hNum = idx + 1;
                const isFocused = activeInspectedCell?.natalHouse === hNum;
                return (
                  <th 
                    key={hNum} 
                    className={`py-2.5 px-2 text-center font-mono border-r border-astro-border/60 transition-colors ${
                      isFocused ? 'bg-amber-500/20 text-astro-primary font-extrabold' : ''
                    }`}
                    scope="col"
                  >
                    H{hNum}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {matrixData.map((row) => {
              const isPlanetSelected = selectedTransitPlanet === row.planet;
              return (
                <tr 
                  key={row.planet} 
                  className={`border-b border-astro-border/70 transition-colors ${
                    isPlanetSelected ? 'bg-astro-primary/5' : 'hover:bg-astro-card/50'
                  }`}
                >
                  {/* Sticky Planet Column */}
                  <th 
                    scope="row"
                    onClick={() => {
                      if (onSelectTransitPlanet) onSelectTransitPlanet(row.planet);
                    }}
                    className={`py-2.5 px-3 sticky left-0 z-10 bg-astro-bg border-r border-astro-border cursor-pointer transition-colors ${
                      isPlanetSelected ? 'bg-amber-500/10 font-bold' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-astro-primary text-xs">{row.planet}</span>
                        {row.isRetrograde && (
                          <span className="text-[9px] px-1 rounded bg-rose-50 text-rose-700 border border-rose-200 font-mono font-bold">
                            ℞
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-astro-secondary-text font-mono">
                        {row.sign.slice(0, 3)} (H{row.placedHouse})
                      </span>
                    </div>
                  </th>

                  {/* 12 House Cells */}
                  {row.cells.map((cell) => {
                    const isCellSelected = selectedCell?.planet === row.planet && selectedCell?.house === cell.natalHouse;

                    // Compute ARIA accessible label
                    const ariaLabel = `${row.planet}, Natal House ${cell.natalHouse}, ${
                      cell.occupies ? 'Occupies' : ''
                    } ${cell.drishti ? (cell.drishtiLabel || 'Aspect') : ''} ${
                      cell.yutiNatalPlanets.length > 0 ? `Yuti with ${cell.yutiNatalPlanets.join(', ')}` : ''
                    } ${!cell.hasRelationship ? 'No direct relationship' : ''}`;

                    return (
                      <td
                        key={cell.natalHouse}
                        tabIndex={0}
                        role="button"
                        aria-label={ariaLabel}
                        onClick={() => handleCellClick(row.planet, cell.natalHouse)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleCellClick(row.planet, cell.natalHouse);
                          }
                        }}
                        className={`py-2 px-1.5 text-center border-r border-astro-border/50 cursor-pointer select-none transition-all outline-hidden focus:ring-2 focus:ring-astro-primary/40 ${
                          isCellSelected
                            ? 'bg-amber-500/25 ring-2 ring-amber-500/50'
                            : cell.occupies
                            ? 'bg-emerald-500/10 hover:bg-emerald-500/20'
                            : cell.drishti
                            ? 'bg-purple-500/10 hover:bg-purple-500/20'
                            : 'hover:bg-astro-card/80 text-stone-300 dark:text-stone-700'
                        }`}
                      >
                        {cell.occupies ? (
                          <div className="flex flex-col items-center justify-center gap-0.5">
                            <span className="inline-flex items-center justify-center w-5 h-5 rounded-md bg-emerald-600 text-white font-bold text-[10px] shadow-2xs">
                              <MapPin className="w-3 h-3" />
                            </span>
                            {cell.yutiNatalPlanets.length > 0 && (
                              <span className="text-[9px] font-bold text-amber-700 dark:text-amber-300 font-mono line-clamp-1">
                                +{cell.yutiNatalPlanets[0].slice(0, 2)}
                              </span>
                            )}
                          </div>
                        ) : cell.drishti ? (
                          <div className="flex flex-col items-center justify-center gap-0.5">
                            <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-md bg-purple-100 dark:bg-purple-950/60 text-purple-900 dark:text-purple-200 border border-purple-300 dark:border-purple-800 font-bold text-[9px]">
                              {cell.drishtiLabel?.split(' ')[0] || 'D'}
                            </span>
                            {cell.drishtiNatalPlanets.length > 0 && (
                              <span className="text-[8.5px] font-semibold text-purple-700 dark:text-purple-300 line-clamp-1">
                                ➔{cell.drishtiNatalPlanets[0].slice(0, 2)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-stone-300 dark:text-stone-600 text-[11px] font-mono">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 2. Mobile Planet-First Relationship Accordion / Selector */}
      <div className="sm:hidden space-y-3">
        <div className="flex items-center justify-between gap-2">
          <label className="text-xs font-semibold text-astro-secondary-text">
            {isHi ? 'ग्रह चुनें:' : 'Select Planet:'}
          </label>
          <select
            value={mobileViewPlanet}
            onChange={(e) => {
              const p = e.target.value as PlanetName;
              setMobileViewPlanet(p);
              if (onSelectTransitPlanet) onSelectTransitPlanet(p);
            }}
            className="bg-astro-card border border-astro-border rounded-lg px-2.5 py-1 text-xs font-bold text-astro-primary focus:outline-none focus:ring-2 focus:ring-astro-primary/30"
          >
            {all9Planets.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        {/* 12-House Grid for Selected Mobile Planet */}
        {(() => {
          const row = matrixData.find(r => r.planet === mobileViewPlanet);
          if (!row) return null;

          return (
            <div className="grid grid-cols-3 gap-2">
              {row.cells.map((cell) => {
                const isSelected = selectedCell?.planet === row.planet && selectedCell?.house === cell.natalHouse;
                return (
                  <button
                    key={cell.natalHouse}
                    onClick={() => handleCellClick(row.planet, cell.natalHouse)}
                    className={`p-2 rounded-xl border text-left transition-all flex flex-col justify-between space-y-1 ${
                      isSelected
                        ? 'bg-amber-500/20 border-amber-500 ring-1 ring-amber-500'
                        : cell.occupies
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-950 dark:text-emerald-200'
                        : cell.drishti
                        ? 'bg-purple-500/10 border-purple-500/30 text-purple-950 dark:text-purple-200'
                        : 'bg-astro-card border-astro-border text-astro-secondary-text'
                    }`}
                  >
                    <div className="flex items-center justify-between text-[11px] font-bold">
                      <span>H{cell.natalHouse}</span>
                      {cell.occupies ? (
                        <MapPin className="w-3 h-3 text-emerald-600" />
                      ) : cell.drishti ? (
                        <Eye className="w-3 h-3 text-purple-600" />
                      ) : null}
                    </div>

                    <div className="text-[10px]">
                      {cell.occupies ? (
                        <span className="font-bold text-emerald-700 dark:text-emerald-300">
                          {isHi ? 'स्थान' : 'Occupies'}
                        </span>
                      ) : cell.drishti ? (
                        <span className="font-semibold text-purple-700 dark:text-purple-300">
                          {cell.drishtiLabel}
                        </span>
                      ) : (
                        <span className="text-stone-400 dark:text-stone-600">—</span>
                      )}
                    </div>

                    {cell.yutiNatalPlanets.length > 0 && (
                      <div className="text-[9px] text-amber-700 dark:text-amber-300 font-bold truncate">
                        Yuti: {cell.yutiNatalPlanets.join(', ')}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* 3. Interactive Detail & Provenance Inspector for Active Selected Cell */}
      {activeInspectedCell && (
        <div className="bg-astro-card border border-astro-primary/30 rounded-xl p-3.5 shadow-2xs space-y-2 animate-fade-in">
          <div className="flex items-center justify-between border-b border-astro-border pb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-astro-accent" />
              <h4 className="font-bold text-astro-primary text-xs sm:text-sm">
                {isHi ? 'संबंध विवरण:' : 'Relationship Detail:'}{' '}
                <span className="text-astro-text font-serif">
                  {activeInspectedCell.transitPlanet} ➔ {isHi ? 'भाव' : 'House'} {activeInspectedCell.natalHouse}
                </span>
              </h4>
            </div>
            <button
              onClick={() => setSelectedCell(null)}
              className="p-1 rounded-md text-astro-secondary-text hover:text-astro-text hover:bg-astro-border/50"
              title={isHi ? 'बंद करें' : 'Close detail'}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
            <div className="p-2 bg-astro-bg rounded-lg border border-astro-border">
              <span className="text-[10px] text-astro-secondary-text uppercase font-semibold block">
                {isHi ? 'गोचर स्थिति' : 'Transit Position'}
              </span>
              <span className="font-bold text-astro-primary">
                {activeInspectedCell.transitPlanet} in {activeInspectedCell.transitSign} {activeInspectedCell.transitDegree}
                {activeInspectedCell.isRetrograde ? ' (℞)' : ''}
              </span>
            </div>

            <div className="p-2 bg-astro-bg rounded-lg border border-astro-border">
              <span className="text-[10px] text-astro-secondary-text uppercase font-semibold block">
                {isHi ? 'शास्त्रीय संबंध' : 'Classical Sambandha'}
              </span>
              <span className="font-bold text-astro-text">
                {activeInspectedCell.occupies
                  ? (isHi ? 'भाव स्थिति (Occupies House)' : 'Occupies House (Sthana)')
                  : activeInspectedCell.drishti
                  ? activeInspectedCell.drishtiLabel
                  : (isHi ? 'कोई प्रत्यक्ष दृष्टि/स्थिति नहीं' : 'No Direct Aspect/Occupation')}
              </span>
            </div>

            <div className="p-2 bg-astro-bg rounded-lg border border-astro-border">
              <span className="text-[10px] text-astro-secondary-text uppercase font-semibold block">
                {isHi ? 'प्रभावित जन्म ग्रह' : 'Contacted Natal Planets'}
              </span>
              <span className="font-semibold text-purple-700 dark:text-purple-300">
                {activeInspectedCell.occupies && activeInspectedCell.yutiNatalPlanets.length > 0
                  ? `Yuti with ${activeInspectedCell.yutiNatalPlanets.join(', ')}`
                  : activeInspectedCell.drishti && activeInspectedCell.drishtiNatalPlanets.length > 0
                  ? `Drishti on ${activeInspectedCell.drishtiNatalPlanets.join(', ')}`
                  : (isHi ? 'कोई जन्म ग्रह नहीं' : 'None in this house')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* 4. Ashtakavarga BAV/SAV Detail & Classical Modulation Inspector (P2-C) */}
      {(() => {
        const inspectedPlanet: PlanetName = selectedCell ? selectedCell.planet : (selectedTransitPlanet || 'Jupiter');
        const inspectedRow = matrixData.find(r => r.planet === inspectedPlanet);
        const inspectedHouse: number = selectedCell ? selectedCell.house : (inspectedRow?.placedHouse || 1);

        return (
          <AshtakavargaGocharInspector
            kundali={kundali}
            selectedPlanet={inspectedPlanet}
            selectedHouse={inspectedHouse}
            selectedPerspective={selectedPerspective}
            lang={lang}
            onOpenEvidenceDrawer={onOpenEvidenceDrawer}
            transitPlanets={transitPlanets}
          />
        );
      })()}
    </div>
  );
};
